import React from "react";
import { Link, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useRequest } from "../hooks/useRequest";
import { useAuth } from "../context/AuthContext";
import { useLeaguePublicContext } from "./LeaguePublicLayout";
import { getPlayerUrl, getPlayerStatsUrl } from "../config/api";
import {
  Player,
  PlayerCareerStats,
  PlayerSeasonStat,
  GoalieCareerStats,
  GoalieSeasonStat,
} from "../types/models";

export default function PlayerProfilePage() {
  const { playerId, abbr } = useParams<{
    playerId: string;
    abbr: string;
  }>();
  const auth = useAuth();

  const playerIdNum = parseInt(playerId || "0");

  const { setPageDetail } = useLeaguePublicContext();

  // Fetch player details
  const { data: player } = useRequest<Player>(
    getPlayerUrl(playerIdNum),
    auth.token,
  );

  // Put the player's name in the page title once they load.
  React.useEffect(() => {
    if (!player) return;

    setPageDetail(
      [player.first_name, player.last_name].filter(Boolean).join(" "),
    );
  }, [player, setPageDetail]);

  // Fetch player career stats (all seasons) - skater stats
  const { data: careerStats } = useRequest<PlayerCareerStats>(
    getPlayerStatsUrl(playerIdNum, "skater"),
    auth.token,
  );

  // Fetch player career stats (all seasons) - goalie stats
  const { data: goalieCareerStats } = useRequest<GoalieCareerStats>(
    getPlayerStatsUrl(playerIdNum, "goalie"),
    auth.token,
  );

  if (!player) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" variant="secondary" role="status" />
      </div>
    );
  }

  const fullName = `${player.first_name} ${player.last_name}`;
  const seasonsPlayed = careerStats?.seasons.length ?? 0;

  // Helper to combine regular and playoff stats for a season
  const getCombinedStats = (season: PlayerSeasonStat) => ({
    games_played: season.regular.games_played + season.playoff.games_played,
    goals: season.regular.goals + season.playoff.goals,
    assists: season.regular.assists + season.playoff.assists,
    points: season.regular.points + season.playoff.points,
    penalties: season.regular.penalties + season.playoff.penalties,
    penalty_minutes:
      season.regular.penalty_minutes + season.playoff.penalty_minutes,
  });

  // Calculate career highlights (combined regular + playoff)
  const bestPointsSeason =
    careerStats?.seasons && careerStats.seasons.length > 0
      ? careerStats.seasons.reduce((best, current) => {
          const bestCombined = getCombinedStats(best);
          const currentCombined = getCombinedStats(current);
          return currentCombined.points > bestCombined.points ? current : best;
        })
      : undefined;

  const mostGoalsSeason =
    careerStats?.seasons && careerStats.seasons.length > 0
      ? careerStats.seasons.reduce((best, current) => {
          const bestCombined = getCombinedStats(best);
          const currentCombined = getCombinedStats(current);
          return currentCombined.goals > bestCombined.goals ? current : best;
        })
      : undefined;

  const bestPPGSeason =
    careerStats?.seasons && careerStats.seasons.length > 0
      ? careerStats.seasons.reduce((best, current) => {
          const bestCombined = getCombinedStats(best);
          const currentCombined = getCombinedStats(current);
          const currentPPG =
            currentCombined.games_played > 0
              ? currentCombined.points / currentCombined.games_played
              : 0;
          const bestPPG =
            bestCombined.games_played > 0
              ? bestCombined.points / bestCombined.games_played
              : 0;
          return currentPPG > bestPPG ? current : best;
        })
      : undefined;

  const bestPointsCombined = bestPointsSeason
    ? getCombinedStats(bestPointsSeason)
    : null;
  const mostGoalsCombined = mostGoalsSeason
    ? getCombinedStats(mostGoalsSeason)
    : null;
  const bestPPGCombined = bestPPGSeason
    ? getCombinedStats(bestPPGSeason)
    : null;

  const bestPPG =
    bestPPGCombined && bestPPGCombined.games_played > 0
      ? (bestPPGCombined.points / bestPPGCombined.games_played).toFixed(2)
      : "0.00";

  // Helper to combine regular and playoff goalie stats for a season
  const getCombinedGoalieStats = (season: GoalieSeasonStat) => ({
    games_played: season.regular.games_played + season.playoff.games_played,
    wins: season.regular.wins + season.playoff.wins,
    losses: season.regular.losses + season.playoff.losses,
    ties: season.regular.ties + season.playoff.ties,
    goals_against: season.regular.goals_against + season.playoff.goals_against,
    shutouts: season.regular.shutouts + season.playoff.shutouts,
  });

  // Calculate goalie career highlights (combined regular + playoff)
  const mostWinsSeason =
    goalieCareerStats?.seasons && goalieCareerStats.seasons.length > 0
      ? goalieCareerStats.seasons.reduce((best, current) => {
          const bestCombined = getCombinedGoalieStats(best);
          const currentCombined = getCombinedGoalieStats(current);
          return currentCombined.wins > bestCombined.wins ? current : best;
        })
      : undefined;

  const bestGAASeason =
    goalieCareerStats?.seasons && goalieCareerStats.seasons.length > 0
      ? goalieCareerStats.seasons.reduce((best, current) => {
          const bestCombined = getCombinedGoalieStats(best);
          const currentCombined = getCombinedGoalieStats(current);
          // Only consider seasons with at least 5 games played
          if (currentCombined.games_played < 5) return best;
          if (bestCombined.games_played < 5) return current;

          const currentGAA =
            currentCombined.games_played > 0
              ? currentCombined.goals_against / currentCombined.games_played
              : 999;
          const bestGAA =
            bestCombined.games_played > 0
              ? bestCombined.goals_against / bestCombined.games_played
              : 999;
          return currentGAA < bestGAA ? current : best;
        })
      : undefined;

  const mostShutoutsSeason =
    goalieCareerStats?.seasons && goalieCareerStats.seasons.length > 0
      ? goalieCareerStats.seasons.reduce((best, current) => {
          const bestCombined = getCombinedGoalieStats(best);
          const currentCombined = getCombinedGoalieStats(current);
          return currentCombined.shutouts > bestCombined.shutouts
            ? current
            : best;
        })
      : undefined;

  const mostWinsCombined = mostWinsSeason
    ? getCombinedGoalieStats(mostWinsSeason)
    : null;
  const bestGAACombined = bestGAASeason
    ? getCombinedGoalieStats(bestGAASeason)
    : null;
  const mostShutoutsCombined = mostShutoutsSeason
    ? getCombinedGoalieStats(mostShutoutsSeason)
    : null;

  const bestGAA =
    bestGAACombined && bestGAACombined.games_played > 0
      ? (bestGAACombined.goals_against / bestGAACombined.games_played).toFixed(
          2,
        )
      : "0.00";

  const goalieSeasonsPlayed = goalieCareerStats?.seasons.length ?? 0;

  return (
    <div>
      {/* Player Header */}
      <div className="lp-card mb-3">
        <div style={{ padding: "24px" }}>
          <h2
            style={{
              margin: 0,
              marginBottom: "8px",
              fontSize: "28px",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {fullName}
          </h2>
          <div
            style={{
              display: "flex",
              gap: "16px",
              fontSize: "14px",
              color: "var(--text-secondary)",
              marginBottom: careerStats && seasonsPlayed > 0 ? "16px" : "0",
            }}
          >
            {player.position && <div>{player.position}</div>}
            {careerStats && seasonsPlayed > 0 && (
              <>
                {player.position && (
                  <div
                    style={{ color: "var(--border-medium)" }}
                    aria-hidden="true"
                  >
                    ·
                  </div>
                )}
                <div>
                  <strong style={{ color: "var(--text-primary)" }}>
                    {seasonsPlayed}
                  </strong>{" "}
                  {seasonsPlayed === 1 ? "season" : "seasons"}
                </div>
              </>
            )}
          </div>

          {/* Career Highlights - Skater */}
          {careerStats && seasonsPlayed > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "12px",
                marginTop: "16px",
              }}
            >
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "var(--bg-secondary)",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    marginBottom: "4px",
                  }}
                >
                  BEST SEASON (PTS)
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {bestPointsCombined?.points ?? 0}
                </div>
                <div
                  style={{ fontSize: "12px", color: "var(--text-secondary)" }}
                >
                  {bestPointsSeason?.season_name}
                </div>
              </div>

              <div
                style={{
                  padding: "12px",
                  backgroundColor: "var(--bg-secondary)",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    marginBottom: "4px",
                  }}
                >
                  MOST GOALS
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {mostGoalsCombined?.goals ?? 0}
                </div>
                <div
                  style={{ fontSize: "12px", color: "var(--text-secondary)" }}
                >
                  {mostGoalsSeason?.season_name}
                </div>
              </div>

              <div
                style={{
                  padding: "12px",
                  backgroundColor: "var(--bg-secondary)",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    marginBottom: "4px",
                  }}
                >
                  BEST PPG
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {bestPPG}
                </div>
                <div
                  style={{ fontSize: "12px", color: "var(--text-secondary)" }}
                >
                  {bestPPGSeason?.season_name}
                </div>
              </div>
            </div>
          )}

          {/* Career Highlights - Goalie */}
          {goalieCareerStats && goalieSeasonsPlayed > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "12px",
                marginTop: "16px",
              }}
            >
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "var(--bg-secondary)",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    marginBottom: "4px",
                  }}
                >
                  MOST WINS
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {mostWinsCombined?.wins ?? 0}
                </div>
                <div
                  style={{ fontSize: "12px", color: "var(--text-secondary)" }}
                >
                  {mostWinsSeason?.season_name}
                </div>
              </div>

              <div
                style={{
                  padding: "12px",
                  backgroundColor: "var(--bg-secondary)",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    marginBottom: "4px",
                  }}
                >
                  BEST GAA
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {bestGAA}
                </div>
                <div
                  style={{ fontSize: "12px", color: "var(--text-secondary)" }}
                >
                  {bestGAASeason?.season_name}
                </div>
              </div>

              <div
                style={{
                  padding: "12px",
                  backgroundColor: "var(--bg-secondary)",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    marginBottom: "4px",
                  }}
                >
                  MOST SHUTOUTS
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {mostShutoutsCombined?.shutouts ?? 0}
                </div>
                <div
                  style={{ fontSize: "12px", color: "var(--text-secondary)" }}
                >
                  {mostShutoutsSeason?.season_name}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Career Stats - Skater */}
      {careerStats && careerStats.seasons.length > 0 && (
        <div className="lp-card mb-3">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-medium)",
              backgroundColor: "var(--bg-secondary)",
            }}
          >
            <strong style={{ fontSize: "13px", color: "var(--text-primary)" }}>
              {goalieCareerStats && goalieCareerStats.seasons.length > 0
                ? "Career Stats - Skater"
                : "Career Stats"}
            </strong>
          </div>
          <div>
            <table className="lp-table">
              <thead>
                <tr>
                  <th className="lp-th-left">Season</th>
                  <th className="lp-th-left"></th>
                  <th title="Games played">GP</th>
                  <th title="Goals">G</th>
                  <th title="Assists">A</th>
                  <th title="Points">PTS</th>
                  <th title="Points per game">PPG</th>
                  <th title="Penalties">PEN</th>
                  <th title="Penalty minutes">PIM</th>
                </tr>
              </thead>
              <tbody>
                {careerStats.seasons.map((seasonStat) => {
                  const regularPPG =
                    seasonStat.regular.games_played > 0
                      ? (
                          seasonStat.regular.points /
                          seasonStat.regular.games_played
                        ).toFixed(2)
                      : "0.00";
                  const playoffPPG =
                    seasonStat.playoff.games_played > 0
                      ? (
                          seasonStat.playoff.points /
                          seasonStat.playoff.games_played
                        ).toFixed(2)
                      : "0.00";
                  return (
                    <React.Fragment key={seasonStat.season_id}>
                      {/* Regular Season Row */}
                      <tr>
                        <td
                          className="lp-td-left"
                          style={{ fontWeight: 500 }}
                          rowSpan={2}
                        >
                          <div>{seasonStat.season_name}</div>
                          {seasonStat.team_name && seasonStat.team_id && (
                            <Link
                              to={`/leagues/${abbr}/teams/${seasonStat.team_id}`}
                              style={{
                                fontSize: "12px",
                                color: "var(--text-secondary)",
                                textDecoration: "none",
                                marginTop: "2px",
                                display: "block",
                              }}
                            >
                              {seasonStat.team_name}
                            </Link>
                          )}
                          {!seasonStat.team_name && (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "var(--text-secondary)",
                                marginTop: "2px",
                              }}
                            >
                              —
                            </div>
                          )}
                        </td>
                        <td
                          className="lp-td-left"
                          style={{
                            fontSize: "11px",
                            color: "var(--text-muted)",
                          }}
                        >
                          Regular
                        </td>
                        <td style={{ color: "var(--text-secondary)" }}>
                          {seasonStat.regular.games_played}
                        </td>
                        <td>{seasonStat.regular.goals}</td>
                        <td>{seasonStat.regular.assists}</td>
                        <td style={{ fontWeight: 600 }}>
                          {seasonStat.regular.points}
                        </td>
                        <td style={{ color: "var(--text-secondary)" }}>
                          {regularPPG}
                        </td>
                        <td>{seasonStat.regular.penalties}</td>
                        <td>{seasonStat.regular.penalty_minutes}</td>
                      </tr>
                      {/* Playoff Row */}
                      <tr
                        style={{
                          backgroundColor: "var(--bg-secondary)",
                          fontSize: "13px",
                        }}
                      >
                        <td
                          className="lp-td-left"
                          style={{
                            fontSize: "11px",
                            color: "var(--text-muted)",
                          }}
                        >
                          Playoffs
                        </td>
                        <td style={{ color: "var(--text-secondary)" }}>
                          {seasonStat.playoff.games_played}
                        </td>
                        <td>{seasonStat.playoff.goals}</td>
                        <td>{seasonStat.playoff.assists}</td>
                        <td style={{ fontWeight: 600 }}>
                          {seasonStat.playoff.points}
                        </td>
                        <td style={{ color: "var(--text-secondary)" }}>
                          {playoffPPG}
                        </td>
                        <td>{seasonStat.playoff.penalties}</td>
                        <td>{seasonStat.playoff.penalty_minutes}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Career Stats - Goalie */}
      {goalieCareerStats && goalieCareerStats.seasons.length > 0 && (
        <div className="lp-card">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-medium)",
              backgroundColor: "var(--bg-secondary)",
            }}
          >
            <strong style={{ fontSize: "13px", color: "var(--text-primary)" }}>
              Career Stats - Goalie
            </strong>
          </div>
          <div>
            <table className="lp-table">
              <thead>
                <tr>
                  <th className="lp-th-left">Season</th>
                  <th className="lp-th-left"></th>
                  <th title="Games played">GP</th>
                  <th title="Wins">W</th>
                  <th title="Losses">L</th>
                  <th title="Ties">T</th>
                  <th title="Goals Against">GA</th>
                  <th title="Goals Against Average">GAA</th>
                  <th title="Shutouts">SO</th>
                </tr>
              </thead>
              <tbody>
                {goalieCareerStats.seasons.map((seasonStat) => {
                  return (
                    <React.Fragment key={seasonStat.season_id}>
                      {/* Regular Season Row */}
                      <tr>
                        <td
                          className="lp-td-left"
                          style={{ fontWeight: 500 }}
                          rowSpan={2}
                        >
                          <div>{seasonStat.season_name}</div>
                          {seasonStat.team_name && seasonStat.team_id && (
                            <Link
                              to={`/leagues/${abbr}/teams/${seasonStat.team_id}`}
                              style={{
                                fontSize: "12px",
                                color: "var(--text-secondary)",
                                textDecoration: "none",
                                marginTop: "2px",
                                display: "block",
                              }}
                            >
                              {seasonStat.team_name}
                            </Link>
                          )}
                          {!seasonStat.team_name && (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "var(--text-secondary)",
                                marginTop: "2px",
                              }}
                            >
                              —
                            </div>
                          )}
                        </td>
                        <td
                          className="lp-td-left"
                          style={{
                            fontSize: "11px",
                            color: "var(--text-muted)",
                          }}
                        >
                          Regular
                        </td>
                        <td style={{ color: "var(--text-secondary)" }}>
                          {seasonStat.regular.games_played}
                        </td>
                        <td>{seasonStat.regular.wins}</td>
                        <td>{seasonStat.regular.losses}</td>
                        <td>{seasonStat.regular.ties}</td>
                        <td>{seasonStat.regular.goals_against}</td>
                        <td>
                          {seasonStat.regular.goals_against_average.toFixed(2)}
                        </td>
                        <td>{seasonStat.regular.shutouts}</td>
                      </tr>
                      {/* Playoff Row */}
                      <tr
                        style={{
                          backgroundColor: "var(--bg-secondary)",
                          fontSize: "13px",
                        }}
                      >
                        <td
                          className="lp-td-left"
                          style={{
                            fontSize: "11px",
                            color: "var(--text-muted)",
                          }}
                        >
                          Playoffs
                        </td>
                        <td style={{ color: "var(--text-secondary)" }}>
                          {seasonStat.playoff.games_played}
                        </td>
                        <td>{seasonStat.playoff.wins}</td>
                        <td>{seasonStat.playoff.losses}</td>
                        <td>{seasonStat.playoff.ties}</td>
                        <td>{seasonStat.playoff.goals_against}</td>
                        <td>
                          {seasonStat.playoff.goals_against_average.toFixed(2)}
                        </td>
                        <td>{seasonStat.playoff.shutouts}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
